import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import type { CountryCode } from "libphonenumber-js";
import { mapSignerAllowedActionsToApi } from "@modals/app.modal";


const API_PHONE_PATTERN = /^\+\d{1,4}-\d{3,}$/;

const SIGNER_EMAIL_REGEX =
  /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;

@Injectable({
  providedIn: "root",
})
export class TableService {
  public tableInputData: Array<any> = [];
  /** Bumped whenever signer rows change so tagging tools re-evaluate allowed actions. */
  public tableInputDataRevision = 0;
  private _touchInputs$ = new Subject<void>();
  private _tableInputDataChanged$ = new Subject<void>();
  touchInputs$ = this._touchInputs$.asObservable();
  tableInputDataChanged$ = this._tableInputDataChanged$.asObservable();

  private notifyTableInputDataChanged(): void {
    this.tableInputDataRevision++;
    this._tableInputDataChanged$.next();
  }

  replaceTableInputData(rows: Array<any>): void {
    this.tableInputData = rows;
    this.notifyTableInputDataChanged();
  }

  patchTableInputRow(index: number, key: string, value: unknown): void {
    if (index < 0 || index >= this.tableInputData.length) return;
    this.tableInputData[index][key] = value;
    this.tableInputData = [...this.tableInputData];
    this.notifyTableInputDataChanged();
  }

  triggerTouchInputs(): void {
    this._touchInputs$.next();
  }

  syncSignerOrders(rows: Array<{ order?: number }> = this.tableInputData): void {
    rows.forEach((row, index) => {
      row.order = index + 1;
    });
  }

  reorderSigners(fromIndex: number, toIndex: number): void {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= this.tableInputData.length ||
      toIndex >= this.tableInputData.length
    ) {
      return;
    }

    const next = [...this.tableInputData];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.syncSignerOrders(next);
    this.tableInputData = next;
    this.notifyTableInputDataChanged();
  }

  
  areAllSignersValid(): boolean {
    if (!this.tableInputData.length) return false;
    return this.tableInputData.every((item) => {
      const name = (item?.name ?? "").toString().trim();
      const email = (item?.email ?? "").toString().trim().toLowerCase();
      const emailCc = (item?.emailCc ?? "").toString().trim().toLowerCase();
      const role = (item?.role ?? "").toString().trim();
      if (!name || !email || !role) return false;
      if (!SIGNER_EMAIL_REGEX.test(email)) return false;
      if (emailCc && !SIGNER_EMAIL_REGEX.test(emailCc)) return false;
      const phoneDigits = (item?.phone ?? "").toString().replace(/\D/g, "");
      if (
        phoneDigits.length > 0 &&
        (phoneDigits.length < 6 || phoneDigits.length > 15)
      ) {
        return false;
      }
      return true;
    });
  }


  getFormattedNumber(phone: string, countryCode: string): string {
    if (phone === null || phone === undefined || String(phone).trim() === "") {
      return "";
    }
    const national = phone.toString().trim();
    const code = (countryCode || "").toString().trim();
    if (!code) return national;
    if (API_PHONE_PATTERN.test(national)) return national;

    let rest = national;
    if (rest.startsWith(code)) {
      rest = rest.slice(code.length).replace(/^[\s\-]+/, "");
    } else {
      const intl = rest.match(/^\+(\d{1,4})[\s\-]*([\d\s\-]*)$/);
      if (intl && `+${intl[1]}` === code) {
        rest = intl[2] || "";
      }
    }
    const digits = rest.replace(/\D/g, "");
    // No national digits ⇒ treat as no phone (avoid sending "+91" alone — API rejects it).
    if (!digits) return "";
    return `${code}-${digits}`;
  }

 
  formatPhoneForApi(phone: string, defaultCountry: CountryCode = "IN"): string {
    const raw = (phone ?? "").toString().trim();
    if (!raw) return raw;
    if (API_PHONE_PATTERN.test(raw)) return raw;

    let parsed = parsePhoneNumberFromString(raw);
    if (parsed?.isValid()) {
      return `+${parsed.countryCallingCode}-${parsed.nationalNumber}`;
    }
    parsed = parsePhoneNumberFromString(raw, defaultCountry) ?? undefined;
    if (parsed?.isValid()) {
      return `+${parsed.countryCallingCode}-${parsed.nationalNumber}`;
    }
    const digits = raw.replace(/\D/g, "");
    if (digits.length > 0) {
      parsed = parsePhoneNumberFromString(digits, defaultCountry) ?? undefined;
      if (parsed?.isValid()) {
        return `+${parsed.countryCallingCode}-${parsed.nationalNumber}`;
      }
      const fallbackCode = `+${getCountryCallingCode(defaultCountry)}`;
      const combined = this.getFormattedNumber(digits, fallbackCode);
      if (API_PHONE_PATTERN.test(combined)) return combined;
    }
    return "";
  }

  
  optionalPhoneForSignRequest(
    phone: string | null | undefined,
    defaultCountry: CountryCode = "IN"
  ): string | undefined {
    const formatted = this.formatPhoneForApi(phone ?? "", defaultCountry);
    if (!formatted || !API_PHONE_PATTERN.test(formatted)) {
      return undefined;
    }
    return formatted;
  }


  emailCcForSignRequest(user: {
    emailCc?: string;
    emailCC?: string;
  } | null | undefined): string {
    const raw = (user?.emailCc ?? user?.emailCC ?? "")
      .toString()
      .trim()
      .toLowerCase();
    if (!raw || !SIGNER_EMAIL_REGEX.test(raw)) {
      return "";
    }
    return raw;
  }


  allowedActionsForSignRequest(user: {
    role?: string;
    allowedActions?: string;
  } | null | undefined): string | undefined {
    const role = (user?.role ?? "").toString().trim().toLowerCase();
    if (role !== "signer") {
      return undefined;
    }
    const raw = (user?.allowedActions ?? "").toString().trim();
    return mapSignerAllowedActionsToApi(raw || "All");
  }

  constructor() {}
}
